import React, {useState, useEffect, useCallback, useRef} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {toCdnUrl} from '../../utils/cdnUrl'

// Public, code-scoped mobile companion. Opened by scanning the QR shown in the
// desktop campaign view. Lets a phone (not logged in) browse the account's
// campaigns → posts → photos and add / reorder / rename / delete media. The
// unguessable :code in the URL is the only credential; every call re-validates it.
//
// Routes (see AppRoutes.tsx):
//   /m/:code                  → campaigns grid
//   /m/:code/campaign/:cid    → posts grid
//   /m/:code/post/:pid        → photos (deep-link target of the per-post QR)

const API_URL = process.env.REACT_APP_API_URL

type Campaign = {_id: string; name: string; color?: string | null; post_count: number; cover?: string | null}
type PostTile = {_id: string; name: string; thumb?: string | null; count: number}
type PostDetail = {_id: string; name: string; images: string[]; post_content: string; reply_text: string; campaign_id: string; campaign_name: string; cap: number}

// api_token stored by the auto-login (?t=) — used to call the authenticated AI endpoint.
function authToken(): string | null {
  try { return JSON.parse(localStorage.getItem('kt-auth-react-v') || '{}')?.api_token || null } catch { return null }
}

const GRAD = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
const GRAD_PINK = 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'

async function api(path: string, opts: RequestInit = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    headers: {'Content-Type': 'application/json', ...(opts.headers || {})},
    ...opts,
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err: any = new Error(data?.error || 'request failed')
    err.status = res.status
    err.expired = res.status === 410 || data?.expired
    throw err
  }
  return data
}

const PostUploadPage: React.FC = () => {
  const {code, cid, pid, did} = useParams<{code: string; cid?: string; pid?: string; did?: string}>()
  const navigate = useNavigate()
  // A draft session behaves exactly like the photos view, but its media lives in the
  // session's staging bucket instead of on a post — the post doesn't exist yet.
  const isDraft = !!did
  const mode: 'campaigns' | 'posts' | 'photos' = (pid || did) ? 'photos' : cid ? 'posts' : 'campaigns'
  const mediaBase = isDraft ? `/mobile-upload/${code}/draft/${did}` : `/mobile-upload/${code}/post/${pid}`

  const [loading, setLoading] = useState(true)
  const [expired, setExpired] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [posts, setPosts] = useState<PostTile[]>([])
  const [campaignName, setCampaignName] = useState('')
  const [post, setPost] = useState<PostDetail | null>(null)

  const [uploading, setUploading] = useState<{done: number; total: number} | null>(null)
  const [busy, setBusy] = useState(false)
  const [lightbox, setLightbox] = useState<number | null>(null) // index of image shown big

  // Inline post-name editing
  const [editingName, setEditingName] = useState(false)
  const [nameInput, setNameInput] = useState('')

  // Drag-and-drop reorder — pointer-based via a grab handle so it works on touch
  // (HTML5 drag events don't fire on phones).
  const [dragIdx, setDragIdx] = useState<number | null>(null)
  const dragInfo = useRef<{from: number; moved: boolean} | null>(null)

  // Quick-switch (photos view): sibling posts in the campaign + all campaigns.
  const [siblings, setSiblings] = useState<PostTile[]>([])
  const [allCampaigns, setAllCampaigns] = useState<Campaign[]>([])
  const [showCampaignSheet, setShowCampaignSheet] = useState(false)

  // Create campaign / post
  const [createSheet, setCreateSheet] = useState<null | 'campaign' | 'post'>(null)
  const [createName, setCreateName] = useState('')
  const [createContent, setCreateContent] = useState('')
  const [createPostCampaign, setCreatePostCampaign] = useState<string | null>(null) // target campaign for a new post
  const [creating, setCreating] = useState(false)

  // Post text + reply text + AI
  const [postText, setPostText] = useState('')
  const [replyText, setReplyText] = useState('')
  const [savingText, setSavingText] = useState(false)
  const [aiBusy, setAiBusy] = useState(false)

  const galleryRef = useRef<HTMLInputElement>(null)
  const cameraRef = useRef<HTMLInputElement>(null)

  // Auto-login: the QR carries an api_token (?t=…) so the phone is signed into the
  // account without a login screen. Store it the same way the app does, then strip
  // it from the URL so it isn't left in the address bar or re-shared.
  useEffect(() => {
    // This page renders OUTSIDE the <App> wrapper, so the HTML boot splash
    // (#splash-screen in index.html) is never auto-hidden — do it here.
    const splashScreen = document.getElementById('splash-screen')
    if (splashScreen) splashScreen.style.display = 'none'

    const params = new URLSearchParams(window.location.search)
    const t = params.get('t')
    if (t) {
      try { localStorage.setItem('kt-auth-react-v', JSON.stringify({api_token: t})) } catch {}
      params.delete('t')
      const clean = window.location.pathname + (params.toString() ? `?${params.toString()}` : '')
      window.history.replaceState({}, '', clean)
    }
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      if (mode === 'campaigns') {
        const d = await api(`/mobile-upload/${code}/campaigns`)
        setCampaigns(d.campaigns || [])
      } else if (mode === 'posts') {
        const d = await api(`/mobile-upload/${code}/campaign/${cid}/posts`)
        setPosts(d.posts || [])
        setCampaignName(d.campaign?.name || '')
      } else if (mode === 'photos' && isDraft) {
        // Staging bucket for a post still open in the desktop composer. There's no post
        // record yet, so everything post-shaped (name, text, campaign) is left blank and
        // the UI that edits those is hidden — this view is photos only.
        const d = await api(`${mediaBase}`)
        setPost({
          _id: '', name: '', images: d.draft?.images || [], post_content: '', reply_text: '',
          campaign_id: '', campaign_name: '', cap: d.draft?.cap || 30,
        })
      } else if (mode === 'photos') {
        const d = await api(`/mobile-upload/${code}/post/${pid}`)
        setPost(d.post)
        setPostText(d.post?.post_content || '')
        setReplyText(d.post?.reply_text || '')
      }
    } catch (e: any) {
      if (e.expired) setExpired(true)
      else setError(e.message || 'שגיאה')
    } finally {
      setLoading(false)
    }
  }, [code, cid, pid, did, mode, isDraft, mediaBase])

  useEffect(() => { load() }, [load])

  // For the photos view: load the campaign's other posts (for the quick post
  // strip) and all campaigns (for the campaign switcher).
  useEffect(() => {
    if (mode !== 'photos' || !post?.campaign_id) return
    let cancel = false
    ;(async () => {
      try {
        const [pc, cc] = await Promise.all([
          api(`/mobile-upload/${code}/campaign/${post.campaign_id}/posts`),
          api(`/mobile-upload/${code}/campaigns`),
        ])
        if (cancel) return
        setSiblings(pc.posts || [])
        setAllCampaigns(cc.campaigns || [])
      } catch { /* non-critical */ }
    })()
    return () => { cancel = true }
  }, [mode, post?.campaign_id, code])

  // ---- mutations -----------------------------------------------------------
  const uploadFiles = async (files: FileList | null) => {
    if (!files || !files.length || !post) return
    const list = Array.from(files)
    setUploading({done: 0, total: list.length})
    let latest = post.images
    try {
      for (let i = 0; i < list.length; i++) {
        const fd = new FormData()
        fd.append('file', list[i])
        const res = await fetch(`${API_URL}${mediaBase}/file`, {method: 'POST', body: fd})
        const data = await res.json().catch(() => ({}))
        if (!res.ok) {
          if (res.status === 410) { setExpired(true); return }
          throw new Error(data?.error || 'העלאה נכשלה')
        }
        latest = data.images
        setPost((p) => (p ? {...p, images: data.images} : p))
        setUploading({done: i + 1, total: list.length})
      }
    } catch (e: any) {
      alert(e.message || 'שגיאה בהעלאה')
    } finally {
      setUploading(null)
      setPost((p) => (p ? {...p, images: latest} : p))
    }
  }

  const reorder = async (next: string[]) => {
    if (!post) return
    const prev = post.images
    setPost({...post, images: next}) // optimistic
    setBusy(true)
    try {
      await api(`${mediaBase}/order`, {method: 'PUT', body: JSON.stringify({images: next})})
    } catch (e: any) {
      setPost({...post, images: prev})
      if (e.expired) setExpired(true)
      else alert(e.message || 'שגיאה בסידור')
    } finally {
      setBusy(false)
    }
  }

  const moveTo = (from: number, to: number) => {
    if (!post || from === to || from < 0 || to < 0 || from >= post.images.length || to >= post.images.length) return
    const next = [...post.images]
    const [img] = next.splice(from, 1)
    next.splice(to, 0, img)
    reorder(next)
  }

  const makeCover = (idx: number) => {
    if (!post || idx === 0) return
    moveTo(idx, 0)
    setLightbox(0)
  }

  const removeImage = async (idx: number) => {
    if (!post) return
    const url = post.images[idx]
    const prev = post.images
    const nextImgs = post.images.filter((_, i) => i !== idx)
    setPost({...post, images: nextImgs}) // optimistic
    setLightbox(nextImgs.length ? Math.min(idx, nextImgs.length - 1) : null)
    setBusy(true)
    try {
      const d = await api(`${mediaBase}/image`, {method: 'DELETE', body: JSON.stringify({url})})
      setPost((p) => (p ? {...p, images: d.images} : p))
    } catch (e: any) {
      setPost({...post, images: prev})
      if (e.expired) setExpired(true)
      else alert(e.message || 'שגיאה במחיקה')
    } finally {
      setBusy(false)
    }
  }

  const saveName = async () => {
    if (!post) return
    const name = nameInput.trim()
    setEditingName(false)
    if (name === (post.name || '')) return
    setPost({...post, name})
    try {
      await api(`/mobile-upload/${code}/post/${pid}/name`, {method: 'PUT', body: JSON.stringify({name})})
    } catch (e: any) {
      if (e.expired) setExpired(true)
      else alert(e.message || 'שגיאה בשמירת השם')
    }
  }

  // ---- create campaign / post ----------------------------------------------
  const doCreate = async () => {
    const name = createName.trim()
    if (!name && createSheet === 'campaign') { alert('יש להזין שם קמפיין'); return }
    setCreating(true)
    try {
      if (createSheet === 'campaign') {
        const d = await api(`/mobile-upload/${code}/campaigns`, {method: 'POST', body: JSON.stringify({name})})
        setCreateSheet(null); setCreateName('')
        navigate(`/m/${code}/campaign/${d.campaign._id}`)
      } else if (createSheet === 'post') {
        const targetCid = createPostCampaign || cid
        const d = await api(`/mobile-upload/${code}/campaign/${targetCid}/posts`, {method: 'POST', body: JSON.stringify({name, post_content: createContent})})
        setCreateSheet(null); setCreateName(''); setCreateContent(''); setCreatePostCampaign(null)
        navigate(`/m/${code}/post/${d.post._id}`)
      }
    } catch (e: any) {
      if (e.expired) setExpired(true)
      else alert(e.message || 'שגיאה ביצירה')
    } finally {
      setCreating(false)
    }
  }

  // ---- post text + reply + AI ----------------------------------------------
  const saveText = async () => {
    setSavingText(true)
    try {
      await api(`/mobile-upload/${code}/post/${pid}/text`, {method: 'PUT', body: JSON.stringify({post_content: postText, reply_text: replyText})})
      setPost((p) => (p ? {...p, post_content: postText, reply_text: replyText} : p))
    } catch (e: any) {
      if (e.expired) setExpired(true)
      else alert(e.message || 'שגיאה בשמירה')
    } finally {
      setSavingText(false)
    }
  }

  const improveWithAI = async () => {
    if (!postText.trim()) { alert('כתבו טקסט לפני שיפור'); return }
    const token = authToken()
    if (!token) { alert('נדרשת התחברות (פתחו דרך קוד ה-QR)'); return }
    setAiBusy(true)
    try {
      const res = await fetch(`${API_URL}/api/enhance-post-content`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
        body: JSON.stringify({content: postText}),
      })
      const data = await res.json().catch(() => ({}))
      if (data?.success && data.enhanced_content) {
        setPostText(data.enhanced_content)
      } else {
        alert(data?.error || 'שירות ה-AI אינו זמין כרגע')
      }
    } catch {
      alert('שגיאה בשיפור עם AI')
    } finally {
      setAiBusy(false)
    }
  }

  // Pointer-based drag reorder (works on touch + mouse). The dragged image
  // moves through the array live as the finger/cursor passes over other tiles;
  // the new order is committed to the server on release.
  const startDrag = (e: React.PointerEvent, idx: number) => {
    e.stopPropagation()
    dragInfo.current = {from: idx, moved: false}
    setDragIdx(idx)
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch {}
  }
  const onDragMove = (e: React.PointerEvent) => {
    if (!dragInfo.current || !post) return
    const under = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null
    const tile = under?.closest('[data-photo-idx]') as HTMLElement | null
    if (!tile) return
    const to = parseInt(tile.getAttribute('data-photo-idx') || '-1', 10)
    const from = dragInfo.current.from
    if (to >= 0 && to !== from) {
      const next = [...post.images]
      const [img] = next.splice(from, 1)
      next.splice(to, 0, img)
      dragInfo.current = {from: to, moved: true}
      setDragIdx(to)
      setPost({...post, images: next})
    }
  }
  const endDrag = () => {
    const info = dragInfo.current
    dragInfo.current = null
    setDragIdx(null)
    if (info?.moved && post) reorder(post.images)
  }

  // ---- expired screen ------------------------------------------------------
  if (expired) {
    return (
      <Shell>
        <div style={{textAlign: 'center', padding: '80px 24px'}}>
          <div style={{fontSize: 56, marginBottom: 16}}>⌛</div>
          <h2 style={{color: '#1a1a2e', margin: 0}}>הקישור פג תוקף</h2>
          <p style={{color: '#888', marginTop: 8}}>סרקו שוב את קוד ה-QR מהמחשב כדי להמשיך.</p>
        </div>
      </Shell>
    )
  }

  const images = post?.images || []

  return (
    <Shell>
      {/* Compact header */}
      <div style={{background: GRAD, color: '#fff', padding: '12px 14px', position: 'sticky', top: 0, zIndex: 5, display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 2px 10px rgba(102,126,234,0.3)'}}>
        {mode !== 'campaigns' && (
          <button onClick={() => navigate(-1)} aria-label="חזרה" style={backBtn}>‹</button>
        )}
        <div style={{width: 36, height: 36, borderRadius: 10, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 1px 4px rgba(0,0,0,0.15)'}}>
          <img src="/media/logos/lightning-blue.svg" alt="FB Zipper" style={{width: 22, height: 22}} />
        </div>
        <div style={{flex: 1, minWidth: 0}}>
          <div style={{fontSize: 12, opacity: 0.95, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
            {mode === 'campaigns' && '📤 העלאת תמונות מהטלפון'}
            {mode === 'posts' && '📁 בחר פוסט'}
            {mode === 'photos' && isDraft && '📤 העלאה למחשב'}
            {mode === 'photos' && !isDraft && (
              <span onClick={() => setShowCampaignSheet(true)} style={{cursor: 'pointer'}}>
                📁 {post?.campaign_name || '...'} <span style={{opacity: 0.85}}>▾ החלף קמפיין</span>
              </span>
            )}
          </div>
          <div style={{fontSize: 19, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
            {mode === 'campaigns' && 'הקמפיינים שלי'}
            {mode === 'posts' && (campaignName || '...')}
            {mode === 'photos' && isDraft && 'תמונות לפוסט החדש'}
            {mode === 'photos' && !isDraft && (
              editingName ? (
                <input
                  autoFocus value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  onBlur={saveName}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveName() }}
                  placeholder="שם הפוסט"
                  style={{width: '100%', border: 'none', borderRadius: 8, padding: '4px 8px', fontSize: 17, fontWeight: 700, color: '#1a1a2e', outline: 'none'}}
                />
              ) : (
                <span onClick={() => { setNameInput(post?.name || ''); setEditingName(true) }} style={{cursor: 'pointer'}}>
                  {post?.name || <span style={{opacity: 0.7, fontWeight: 600}}>＋ הוסף שם לפוסט</span>} <span style={{fontSize: 13, opacity: 0.8}}>✏️</span>
                </span>
              )
            )}
          </div>
        </div>
      </div>

      {/* Quick post switcher — horizontal strip of the campaign's posts */}
      {mode === 'photos' && !isDraft && siblings.length > 1 && (
        <div style={{display: 'flex', gap: 8, overflowX: 'auto', padding: '10px 12px', background: '#fff', borderBottom: '1px solid #eee', WebkitOverflowScrolling: 'touch'}}>
          {siblings.map((s) => {
            const active = s._id === pid
            return (
              <div key={s._id} onClick={() => navigate(`/m/${code}/post/${s._id}`)}
                style={{flex: '0 0 auto', width: 58, textAlign: 'center', cursor: 'pointer'}}>
                <div style={{width: 58, height: 58, borderRadius: 12, overflow: 'hidden', background: '#eceef3', border: active ? '2px solid #f5576c' : '2px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                  {s.thumb ? <img src={toCdnUrl(s.thumb)} alt="" style={{width: '100%', height: '100%', objectFit: 'cover'}} /> : <span style={{opacity: 0.5}}>📝</span>}
                </div>
                <div style={{fontSize: 10, marginTop: 3, color: active ? '#f5576c' : '#8a90a0', fontWeight: active ? 700 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                  {s.name || '—'}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {error && <div style={{color: '#f5576c', padding: 16, textAlign: 'center'}}>{error}</div>}

      <div style={{padding: 12, paddingBottom: 100}}>
        {loading ? (
          <SkeletonGrid cols={mode === 'photos' ? 3 : 2} />
        ) : mode === 'campaigns' ? (
          <>
            <CreateBar label="קמפיין חדש" onClick={() => { setCreateName(''); setCreateSheet('campaign') }} />
            <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
              {campaigns.map((c) => (
                <div key={c._id} style={{minWidth: 0, background: '#fff', borderRadius: 18, overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.08)'}}>
                  <div onClick={() => navigate(`/m/${code}/campaign/${c._id}`)} style={{position: 'relative', cursor: 'pointer'}}>
                    <div style={{width: '100%', aspectRatio: '16 / 9', background: '#eceef3', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      {c.cover ? <img src={toCdnUrl(c.cover)} alt="" style={{width: '100%', height: '100%', objectFit: 'cover'}} /> : <span style={{fontSize: 40, opacity: 0.4}}>🗂️</span>}
                    </div>
                    <span style={{position: 'absolute', top: 10, insetInlineStart: 10, display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(0,0,0,0.62)', color: '#fff', padding: '4px 11px', borderRadius: 20, fontSize: 12.5, fontWeight: 700, backdropFilter: 'blur(4px)'}}>
                      📄 {c.post_count} פוסטים
                    </span>
                  </div>
                  <div style={{padding: 14}}>
                    <div style={{fontSize: 17, fontWeight: 800, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 12}}>{c.name}</div>
                    <button onClick={() => { setCreateName(''); setCreateContent(''); setCreatePostCampaign(c._id); setCreateSheet('post') }} style={rowBtnPrimary}>
                      ＋ הוסף פוסט חדש
                    </button>
                    <button onClick={() => navigate(`/m/${code}/campaign/${c._id}`)} style={rowBtnSecondary}>
                      צפה בפוסטים
                    </button>
                  </div>
                </div>
              ))}
              {!campaigns.length && <Empty text="אין קמפיינים עדיין" />}
            </div>
          </>
        ) : mode === 'posts' ? (
          <>
            <CreateBar label="פוסט חדש" onClick={() => { setCreateName(''); setCreateContent(''); setCreatePostCampaign(cid || null); setCreateSheet('post') }} />
            <Grid cols={2}>
              {posts.map((p) => (
                <Card key={p._id} onClick={() => navigate(`/m/${code}/post/${p._id}`)}>
                  <Thumb src={p.thumb} fallback="📝" badge={p.count ? `${p.count}` : undefined} ratio="4 / 3" />
                  <CardLabel title={p.name || 'ללא שם'} sub={p.count ? `${p.count} תמונות` : 'אין תמונות'} />
                </Card>
              ))}
              {!posts.length && <Empty text="אין פוסטים עדיין" />}
            </Grid>
          </>
        ) : (
          // photos — 3-col grid, drag to reorder, tap to view big
          <>
            {images.length > 1 && (
              <div style={{fontSize: 12, color: '#9aa0ab', marginBottom: 8, textAlign: 'center'}}>
                גררו את הידית <span style={{fontSize: 14}}>⠿</span> לסידור · הראשונה היא הראשית · הקישו לתצוגה
              </div>
            )}
            <Grid cols={3}>
              {images.map((img, idx) => (
                <div
                  key={img + idx}
                  data-photo-idx={idx}
                  onClick={() => setLightbox(idx)}
                  style={{...photoTile, opacity: dragIdx === idx ? 0.35 : 1, transform: dragIdx === idx ? 'scale(0.96)' : 'none', transition: 'transform 0.12s'}}
                >
                  <img src={toCdnUrl(img)} alt="" draggable={false} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                  {idx === 0 && <span style={coverBadge}>ראשי</span>}
                  <div
                    onPointerDown={(e) => startDrag(e, idx)}
                    onPointerMove={onDragMove}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    onClick={(e) => e.stopPropagation()}
                    title="גרור לסידור"
                    style={{position: 'absolute', bottom: 6, insetInlineStart: 6, width: 30, height: 30, borderRadius: 8, background: 'rgba(0,0,0,0.55)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, cursor: 'grab', touchAction: 'none'}}
                  >⠿</div>
                </div>
              ))}
              <button onClick={() => galleryRef.current?.click()} style={addTile}>
                <span style={{fontSize: 28, lineHeight: 1}}>＋</span>
                <span style={{fontSize: 11, marginTop: 4}}>הוסף</span>
              </button>
            </Grid>

            {/* Post text + reply text + AI — needs a saved post, so not in draft mode */}
            {!isDraft && <div style={{marginTop: 18, background: '#fff', borderRadius: 16, padding: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.06)'}}>
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6}}>
                <label style={{fontSize: 13, fontWeight: 700, color: '#1a1a2e'}}>תוכן הפוסט</label>
                <button onClick={improveWithAI} disabled={aiBusy} style={aiBtn(aiBusy)}>
                  {aiBusy ? 'משפר…' : '✨ שפר עם AI'}
                </button>
              </div>
              <textarea
                value={postText} onChange={(e) => setPostText(e.target.value)}
                placeholder="כתבו את תוכן הפוסט…" rows={5} style={textArea}
              />

              <label style={{fontSize: 13, fontWeight: 700, color: '#1a1a2e', display: 'block', margin: '14px 0 6px'}}>
                💬 תגובה אוטומטית
              </label>
              <textarea
                value={replyText} onChange={(e) => setReplyText(e.target.value)}
                placeholder="תגובה אוטומטית לכל מי שמגיב…" rows={3} style={textArea}
              />

              <button onClick={saveText} disabled={savingText} style={{
                width: '100%', marginTop: 12, padding: '13px', borderRadius: 12, border: 'none',
                background: GRAD, color: '#fff', fontSize: 15, fontWeight: 700, cursor: savingText ? 'not-allowed' : 'pointer', opacity: savingText ? 0.7 : 1,
              }}>
                {savingText ? 'שומר…' : 'שמור טקסט'}
              </button>
            </div>}
            {isDraft && (
              <div style={{marginTop: 18, background: '#fff', borderRadius: 16, padding: 14, textAlign: 'center', color: '#666', fontSize: 13, lineHeight: 1.6, boxShadow: '0 2px 10px rgba(0,0,0,0.06)'}}>
                ✅ התמונות נשמרות אוטומטית ומופיעות מיד במחשב.<br />
                את שאר פרטי הפוסט אפשר להשלים שם.
              </div>
            )}
          </>
        )}
      </div>

      {/* hidden inputs */}
      <input ref={galleryRef} type="file" accept="image/*" multiple style={{display: 'none'}}
        onChange={(e) => { uploadFiles(e.target.files); e.target.value = '' }} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{display: 'none'}}
        onChange={(e) => { uploadFiles(e.target.files); e.target.value = '' }} />

      {/* uploading overlay */}
      {uploading && (
        <div style={overlay}>
          <div style={{background: '#fff', borderRadius: 16, padding: '24px 32px', textAlign: 'center'}}>
            <div style={{fontSize: 15, fontWeight: 700, color: '#1a1a2e'}}>מעלה תמונות…</div>
            <div style={{color: '#888', marginTop: 6}}>{uploading.done} / {uploading.total}</div>
          </div>
        </div>
      )}

      {/* Lightbox — big image viewer with prev/next + actions */}
      {lightbox !== null && images[lightbox] && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.94)', zIndex: 60, display: 'flex', flexDirection: 'column'}}>
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', color: '#fff'}}>
            <button onClick={() => setLightbox(null)} style={{background: 'none', border: 'none', color: '#fff', fontSize: 26, cursor: 'pointer', lineHeight: 1}}>✕</button>
            <span style={{fontSize: 14, fontWeight: 600}}>{lightbox + 1} / {images.length}</span>
            <span style={{width: 26}} />
          </div>
          <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden'}}>
            {lightbox > 0 && (
              <button onClick={() => setLightbox(lightbox - 1)} style={navArrow('right')}>‹</button>
            )}
            <img src={toCdnUrl(images[lightbox])} alt="" style={{maxWidth: '100%', maxHeight: '100%', objectFit: 'contain'}} />
            {lightbox < images.length - 1 && (
              <button onClick={() => setLightbox(lightbox + 1)} style={navArrow('left')}>›</button>
            )}
            {lightbox === 0 && <span style={{...coverBadge, top: 12, insetInlineStart: 12}}>ראשי</span>}
          </div>
          <div style={{display: 'flex', gap: 10, padding: '14px 18px calc(14px + env(safe-area-inset-bottom))'}}>
            <button onClick={() => makeCover(lightbox)} disabled={lightbox === 0}
              style={lbBtn(lightbox === 0)}>⭐ הפוך לראשית</button>
            <button onClick={() => removeImage(lightbox)} style={{...lbBtn(false), background: 'rgba(245,87,108,0.2)', color: '#ff8aa0'}}>🗑️ מחק</button>
          </div>
        </div>
      )}

      {/* create campaign / post sheet */}
      {createSheet && (
        <div style={overlay} onClick={() => !creating && setCreateSheet(null)}>
          <div style={sheet} onClick={(e) => e.stopPropagation()}>
            <div style={{textAlign: 'center', color: '#1a1a2e', fontSize: 16, fontWeight: 700, marginBottom: 14}}>
              {createSheet === 'campaign' ? 'קמפיין חדש' : 'פוסט חדש'}
            </div>
            <input
              autoFocus value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              onKeyDown={(e) => { if (createSheet === 'campaign' && e.key === 'Enter') doCreate() }}
              placeholder={createSheet === 'campaign' ? 'שם הקמפיין' : 'שם הפוסט (אופציונלי)'}
              style={{width: '100%', padding: '14px 16px', background: '#f5f5f7', border: '2px solid #e4e6ef', borderRadius: 12, fontSize: 15, outline: 'none'}}
            />
            {createSheet === 'post' && (
              <textarea
                value={createContent}
                onChange={(e) => setCreateContent(e.target.value)}
                placeholder="תוכן הפוסט…"
                rows={5}
                style={{...textArea, marginTop: 10}}
              />
            )}
            <button onClick={doCreate} disabled={creating} style={{
              width: '100%', marginTop: 12, padding: '14px', borderRadius: 12, border: 'none',
              background: GRAD, color: '#fff', fontSize: 15, fontWeight: 700, cursor: creating ? 'not-allowed' : 'pointer', opacity: creating ? 0.7 : 1,
            }}>
              {creating ? 'יוצר…' : 'צור'}
            </button>
            <SheetBtn label="ביטול" onClick={() => setCreateSheet(null)} />
          </div>
        </div>
      )}

      {/* campaign switcher sheet */}
      {showCampaignSheet && (
        <div style={overlay} onClick={() => setShowCampaignSheet(false)}>
          <div style={sheet} onClick={(e) => e.stopPropagation()}>
            <div style={{textAlign: 'center', color: '#999', fontSize: 12, marginBottom: 10}}>בחר קמפיין</div>
            <div style={{maxHeight: '55vh', overflowY: 'auto'}}>
              {allCampaigns.map((c) => {
                const active = c._id === post?.campaign_id
                return (
                  <button key={c._id} onClick={() => { setShowCampaignSheet(false); navigate(`/m/${code}/campaign/${c._id}`) }}
                    style={{display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: 10, marginBottom: 8, border: 'none', borderRadius: 12, background: active ? 'rgba(245,87,108,0.08)' : '#f4f5f7', cursor: 'pointer', textAlign: 'right'}}>
                    <div style={{width: 40, height: 40, borderRadius: 10, overflow: 'hidden', background: '#eceef3', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      {c.cover ? <img src={toCdnUrl(c.cover)} alt="" style={{width: '100%', height: '100%', objectFit: 'cover'}} /> : <span style={{opacity: 0.5}}>🗂️</span>}
                    </div>
                    <div style={{flex: 1, minWidth: 0}}>
                      <div style={{fontWeight: 700, color: '#1a1a2e', fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{c.name}</div>
                      <div style={{fontSize: 11, color: '#9aa0ab'}}>{c.post_count} פוסטים</div>
                    </div>
                    {active && <i className="bi bi-check-lg" style={{color: '#f5576c'}} />}
                  </button>
                )
              })}
            </div>
            <SheetBtn label="ביטול" onClick={() => setShowCampaignSheet(false)} />
          </div>
        </div>
      )}

      {/* bottom tab bar */}
      <div style={tabBar}>
        <TabBtn icon="🏠" label="כל הקמפיינים" active={mode === 'campaigns'} onClick={() => navigate(`/m/${code}`)} />
        {mode === 'photos' ? (
          <>
            <TabBtn icon="📷" label="צלם" highlight onClick={() => cameraRef.current?.click()} />
            <TabBtn icon="🖼️" label="גלריה" onClick={() => galleryRef.current?.click()} />
          </>
        ) : (
          <div style={{flex: 1, textAlign: 'center', color: '#aaa', fontSize: 12, alignSelf: 'center'}}>
            {mode === 'posts' ? 'בחר פוסט להוספת תמונות' : 'בחר קמפיין'}
          </div>
        )}
      </div>

      {busy && <div style={{position: 'fixed', top: 0, left: 0, right: 0, height: 3, background: GRAD_PINK, zIndex: 70}} />}
    </Shell>
  )
}

// ---- presentational helpers ------------------------------------------------
const Shell: React.FC<{children: React.ReactNode}> = ({children}) => (
  <div dir="rtl" style={{minHeight: '100vh', background: '#f4f5f7', fontFamily: 'system-ui, -apple-system, "Segoe UI", Rubik, Arial, sans-serif', width: '100%', maxWidth: 600, margin: '0 auto', position: 'relative', overflowX: 'hidden'}}>
    <style>{`* { box-sizing: border-box; } @keyframes mpShimmer{0%{background-position:100% 0}100%{background-position:-100% 0}}`}</style>
    {children}
  </div>
)

const Grid: React.FC<{children: React.ReactNode; cols: number}> = ({children, cols}) => (
  // minmax(0, 1fr) (not the default minmax(auto,1fr)) so image intrinsic width
  // can't force tracks wider than the container — prevents horizontal overflow.
  <div style={{display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: 10}}>{children}</div>
)

const Card: React.FC<{children: React.ReactNode; onClick: () => void}> = ({children, onClick}) => (
  <div onClick={onClick} style={{cursor: 'pointer', minWidth: 0, borderRadius: 16, overflow: 'hidden', background: '#fff', boxShadow: '0 2px 10px rgba(0,0,0,0.07)'}}>
    {children}
  </div>
)

const Thumb: React.FC<{src?: string | null; fallback: string; badge?: string; ratio?: string}> = ({src, fallback, badge, ratio = '1 / 1'}) => (
  <div style={{position: 'relative', width: '100%', aspectRatio: ratio, background: '#eceef3', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
    {src ? (
      <img src={toCdnUrl(src)} alt="" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
    ) : (
      <span style={{fontSize: 34, opacity: 0.5}}>{fallback}</span>
    )}
    {badge !== undefined && (
      <span style={{position: 'absolute', top: 8, insetInlineStart: 8, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8}}>{badge}</span>
    )}
  </div>
)

const CardLabel: React.FC<{title: string; sub: string}> = ({title, sub}) => (
  <div style={{padding: '9px 11px'}}>
    <div style={{fontSize: 13.5, fontWeight: 700, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{title}</div>
    <div style={{fontSize: 11.5, color: '#9aa0ab', marginTop: 1}}>{sub}</div>
  </div>
)

const Empty: React.FC<{text: string}> = ({text}) => (
  <div style={{gridColumn: '1 / -1', textAlign: 'center', color: '#aaa', padding: '60px 0'}}>{text}</div>
)

const CreateBar: React.FC<{label: string; onClick: () => void}> = ({label, onClick}) => (
  <button onClick={onClick} style={{width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px', marginBottom: 12, borderRadius: 12, border: '2px dashed #b3a4e0', background: 'linear-gradient(135deg, rgba(102,126,234,0.06), rgba(118,75,162,0.06))', color: '#5b4bbd', fontSize: 14, fontWeight: 700, cursor: 'pointer'}}>
    <span style={{fontSize: 18, lineHeight: 1}}>＋</span> {label}
  </button>
)

const SkeletonGrid: React.FC<{cols: number}> = ({cols}) => (
  <Grid cols={cols}>
    {Array.from({length: cols * 3}).map((_, i) => (
      <div key={i} style={{aspectRatio: '1 / 1', borderRadius: 16, background: 'linear-gradient(90deg,#eceef3 25%,#f6f7f9 37%,#eceef3 63%)', backgroundSize: '400% 100%', animation: 'mpShimmer 1.4s ease infinite'}} />
    ))}
  </Grid>
)

const TabBtn: React.FC<{icon: string; label: string; active?: boolean; highlight?: boolean; onClick: () => void}> = ({icon, label, active, highlight, onClick}) => (
  <button onClick={onClick} style={{flex: 1, border: 'none', background: 'transparent', padding: '8px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, cursor: 'pointer'}}>
    <span style={{fontSize: 22}}>{icon}</span>
    <span style={{fontSize: 11, fontWeight: 600, color: active ? '#667eea' : highlight ? '#f5576c' : '#8a90a0'}}>{label}</span>
  </button>
)

const SheetBtn: React.FC<{label: string; onClick: () => void; disabled?: boolean; danger?: boolean}> = ({label, onClick, disabled, danger}) => (
  <button onClick={onClick} disabled={disabled} style={{
    width: '100%', padding: '15px 16px', marginTop: 8, borderRadius: 12, border: 'none', textAlign: 'center',
    background: danger ? 'rgba(245,87,108,0.1)' : '#f4f5f7', color: danger ? '#f5576c' : '#1a1a2e',
    fontSize: 15, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1,
  }}>{label}</button>
)

// ---- inline style objects --------------------------------------------------
const backBtn: React.CSSProperties = {background: 'rgba(255,255,255,0.22)', border: 'none', color: '#fff', fontSize: 24, fontWeight: 700, width: 36, height: 36, borderRadius: 10, cursor: 'pointer', flexShrink: 0, lineHeight: 1}
const photoTile: React.CSSProperties = {position: 'relative', minWidth: 0, aspectRatio: '1 / 1', borderRadius: 14, overflow: 'hidden', background: '#eceef3', cursor: 'pointer'}
const coverBadge: React.CSSProperties = {position: 'absolute', top: 6, insetInlineStart: 6, background: GRAD_PINK, color: '#fff', fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 8}
const addTile: React.CSSProperties = {aspectRatio: '1 / 1', borderRadius: 14, border: '2px dashed #c3c8d4', background: '#fff', color: '#667eea', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'}
const textArea: React.CSSProperties = {width: '100%', padding: '12px 14px', background: '#f7f8fa', border: '1px solid #e4e6ef', borderRadius: 12, fontSize: 14, color: '#1a1a2e', outline: 'none', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5, direction: 'rtl'}
const rowBtnPrimary: React.CSSProperties = {width: '100%', padding: '13px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', fontSize: 14.5, fontWeight: 700, cursor: 'pointer'}
const rowBtnSecondary: React.CSSProperties = {width: '100%', padding: '13px', marginTop: 8, borderRadius: 12, border: '1.5px solid #d6d9e3', background: '#fff', color: '#5b4bbd', fontSize: 14.5, fontWeight: 700, cursor: 'pointer'}
const aiBtn = (busy: boolean): React.CSSProperties => ({padding: '6px 12px', borderRadius: 9, border: 'none', background: GRAD_PINK, color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1})
const overlay: React.CSSProperties = {position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 30}
const sheet: React.CSSProperties = {width: '100%', maxWidth: 600, background: '#fff', borderTopLeftRadius: 22, borderTopRightRadius: 22, padding: '16px 16px calc(16px + env(safe-area-inset-bottom))'}
const tabBar: React.CSSProperties = {position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 600, background: '#fff', borderTop: '1px solid #e7e9ee', display: 'flex', padding: '4px 8px calc(4px + env(safe-area-inset-bottom))', zIndex: 15}
const navArrow = (side: 'left' | 'right'): React.CSSProperties => ({position: 'absolute', [side]: 6, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 30, width: 44, height: 44, borderRadius: '50%', cursor: 'pointer', lineHeight: 1})
const lbBtn = (disabled: boolean): React.CSSProperties => ({flex: 1, padding: '13px 12px', borderRadius: 12, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1})

export default PostUploadPage
